import Cookies from 'js-cookie';

import { APP_ENVIRONMENT, IS_HTTPS, OT_COUNTRY_OVERRIDE, OT_DOMAIN, OT_SOURCE, YEAR_IN_DAYS } from 'utils/consts';

// Ported from the portal (grafana-com/packages/grafana-com-portal/src/lib/consent/oneTrust.ts) so consent is shared
// with grafana.com and the portal. Keep the two in sync.

const ANALYTICS_GROUP = 'C0002';
const STORAGE_KEY = 'localStorageConsent';
const CONSENT_COOKIE_NAME = 'consent';
const ANALYTICS_KEY = 'analytics';
const ANALYTICS_VERSION = '2';

export const CONSENT_EVENT = 'otconsent';

declare global {
  interface Window {
    __oneTrustInjected?: boolean;
    OnetrustActiveGroups?: string;
    OptanonWrapper?: () => void;
  }
}

export let canTrack = false;

const oneTrustCountries = new Set([
  'US',
  'CA',
  'GB',
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);

export const isOneTrustCountry = (countryCode: string): boolean =>
  oneTrustCountries.has(countryCode.trim().toUpperCase());

const hasStoredConsent = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const readConsentCookie = (): Record<string, string> => {
  try {
    const content = JSON.parse(Cookies.get(CONSENT_COOKIE_NAME) ?? '{}');

    return content && typeof content === 'object' ? content : {};
  } catch {
    return {};
  }
};

const writeConsentCookie = (content: Record<string, string>) => {
  if (Object.keys(content).length === 0) {
    Cookies.remove(CONSENT_COOKIE_NAME, { path: '/' });
    return;
  }

  Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(content), {
    expires: YEAR_IN_DAYS,
    path: '/',
    sameSite: 'Lax',
    secure: IS_HTTPS,
  });
};

const grantConsent = () => {
  canTrack = true;

  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // storage unavailable: the cookie and event still carry the consent
  }

  writeConsentCookie({ ...readConsentCookie(), [ANALYTICS_KEY]: ANALYTICS_VERSION });
  document.dispatchEvent(new CustomEvent(CONSENT_EVENT));
};

const revokeConsent = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing stored to remove
  }

  const { [ANALYTICS_KEY]: _analytics, ...rest } = readConsentCookie();
  writeConsentCookie(rest);

  // Vendors already loaded cannot be unloaded, so a reload is the only way to stop them.
  if (canTrack) {
    window.location.reload();
  }
};

const getCountryCode = async (): Promise<string | undefined> => {
  if (APP_ENVIRONMENT !== 'production' && OT_COUNTRY_OVERRIDE) {
    return OT_COUNTRY_OVERRIDE;
  }

  try {
    const response = await fetch('/region', { credentials: 'omit' });

    if (!response.ok) {
      return undefined;
    }

    return (await response.text()).trim() || undefined;
  } catch {
    return undefined;
  }
};

const injectOneTrust = (source: string, domain: string) => {
  window.OptanonWrapper = () => {
    if (window.OnetrustActiveGroups?.includes(ANALYTICS_GROUP)) {
      grantConsent();
    } else {
      revokeConsent();
    }
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = source;
  script.setAttribute('data-domain-script', domain);
  document.head.appendChild(script);
  window.__oneTrustInjected = true;
};

/** Grants consent where no banner is needed, otherwise loads OneTrust. Every grant dispatches `otconsent`. */
export const startConsent = async (): Promise<void> => {
  if (hasStoredConsent()) {
    grantConsent();
  }

  // Only production has the OneTrust values. Other environments grant consent to everyone, with no banner.
  if (!OT_SOURCE || !OT_DOMAIN) {
    if (APP_ENVIRONMENT !== 'production') {
      grantConsent();
    }

    return;
  }

  const countryCode = await getCountryCode();

  if (!countryCode) {
    return;
  }

  if (!isOneTrustCountry(countryCode)) {
    grantConsent();
    return;
  }

  if (window.__oneTrustInjected) {
    return;
  }

  injectOneTrust(OT_SOURCE, OT_DOMAIN);
};
